import { useState, useEffect } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Helper: Convert base64 to Uint8Array
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export const usePushNotifications = (token) => {
  const [subscription, setSubscription] = useState(null);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isSupported, setIsSupported] = useState(false);
  const [permission, setPermission] = useState('default');
  const [error, setError] = useState(null);

  useEffect(() => {
    // Check if push notifications are supported
    const supported =
      'serviceWorker' in navigator &&
      'PushManager' in window &&
      'Notification' in window;

    setIsSupported(supported);

    if (supported) {
      setPermission(Notification.permission);
    }
  }, []);

  useEffect(() => {
    if (!isSupported || !token) return;

    // Check subscription status
    checkSubscriptionStatus();
  }, [isSupported, token]);

  const checkSubscriptionStatus = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/push-notifications/subscription-status`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setIsSubscribed(response.data.is_subscribed);
    } catch (err) {
      console.error('Error checking subscription status:', err);
    }
  };

  const requestPermission = async () => {
    if (!isSupported) {
      setError('Push-Benachrichtigungen werden nicht unterstützt');
      return false;
    }

    try {
      const permission = await Notification.requestPermission();
      setPermission(permission);

      if (permission !== 'granted') {
        setError('Benachrichtigungsberechtigung wurde verweigert');
        return false;
      }

      return true;
    } catch (err) {
      console.error('Error requesting permission:', err);
      setError('Fehler beim Anfordern der Berechtigung');
      return false;
    }
  };

  const subscribe = async () => {
    if (!isSupported) {
      setError('Push-Benachrichtigungen werden nicht unterstützt');
      return false;
    }

    if (!token) {
      setError('Nicht angemeldet');
      return false;
    }

    try {
      // Request permission if not granted
      if (Notification.permission !== 'granted') {
        const granted = await requestPermission();
        if (!granted) return false;
      }

      // Register service worker
      const registration = await navigator.serviceWorker.register('/service-worker.js');
      await navigator.serviceWorker.ready;

      // Get VAPID public key
      const vapidPublicKey = process.env.REACT_APP_VAPID_PUBLIC_KEY || 
        'MFkwEwYHKoZIzj0CAQYIKoZIzj0DAQcDQgAEKrpiC0X99ZmKMGdYS1ydVeHv4qf54uAdt32EEAWsdzLEu8neNwz4dHwdrq4fL4zI6Fbn1nJWOEn34rXt1L_C-g';

      // Subscribe to push
      const pushSubscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
      });

      setSubscription(pushSubscription);

      // Send subscription to backend
      await axios.post(
        `${API_URL}/api/push-notifications/subscribe`,
        {
          endpoint: pushSubscription.endpoint,
          keys: {
            p256dh: arrayBufferToBase64(pushSubscription.getKey('p256dh')),
            auth: arrayBufferToBase64(pushSubscription.getKey('auth'))
          }
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setIsSubscribed(true);
      setError(null);
      return true;
    } catch (err) {
      console.error('Error subscribing to push:', err);
      setError('Fehler beim Abonnieren der Benachrichtigungen');
      return false;
    }
  };

  const unsubscribe = async () => {
    if (!subscription || !token) return false;

    try {
      // Unsubscribe from push
      await subscription.unsubscribe();

      // Remove subscription from backend
      await axios.delete(`${API_URL}/api/push-notifications/subscribe`, {
        params: { endpoint: subscription.endpoint },
        headers: { Authorization: `Bearer ${token}` }
      });

      setSubscription(null);
      setIsSubscribed(false);
      setError(null);
      return true;
    } catch (err) {
      console.error('Error unsubscribing from push:', err);
      setError('Fehler beim Abmelden der Benachrichtigungen');
      return false;
    }
  };

  return {
    isSupported,
    permission,
    isSubscribed,
    error,
    subscribe,
    unsubscribe,
    requestPermission
  };
};

// Helper: Convert ArrayBuffer to Base64
function arrayBufferToBase64(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary);
}

export default usePushNotifications;
